<script setup lang="ts">
import { getHostingClients } from '@/services/eos/site-map'
import type { IHostingClient } from '@/types/eos/SiteMap'
import type { IHostingClientFilters } from '@/types/eos/filters'
import { ElMessage } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    placeholder?: string
    multiple?: boolean
  }>(),
  {
    placeholder: 'Select Hosting Client',
    multiple: false,
  }
)

const selectedValue = defineModel<IHostingClient | IHostingClient[]>()

const filterLoading = ref(false)
const filterOptions = ref<OptionType[]>([])

const filterValues = async (query: string) => {
  try {
    filterLoading.value = true

    const filters: IHostingClientFilters = {
      page: 1,
      per_page: 1000,
      order_by: 'name',
    }

    if (query) {
      filters.name = query
    }

    const filteredResources = await getHostingClients(filters)
    filterOptions.value = filteredResources.data.map((res: IHostingClient) => {
      return {
        label: res.name,
        value: res,
      }
    })
  } catch {
    ElMessage.error('Failed to filter hosting clients')
    filterOptions.value = []
  } finally {
    filterLoading.value = false
  }
}

// Add a watch to check selectedValue changes
watch(selectedValue, () => {
  const value = selectedValue.value
  if (!value) return

  if (props.multiple && Array.isArray(value)) {
    // For multiple selection, add each selected value to options
    value.forEach((client) => {
      if (!filterOptions.value.find((option) => option.value.id === client.id)) {
        filterOptions.value.push({
          label: client.name,
          value: client,
        })
      }
    })
  } else if (!props.multiple && !Array.isArray(value)) {
    // For single selection
    if (!filterOptions.value.find((option) => option.value.id === value.id)) {
      filterOptions.value.push({
        label: value.name,
        value: value,
      })
    }
  }
})
</script>

<template>
  <el-select-v2
    v-model="selectedValue"
    :options="filterOptions"
    :loading="filterLoading"
    remote
    :remote-method="filterValues"
    filterable
    clearable
    value-key="id"
    :placeholder="props.placeholder"
    loading-text="Loading..."
    :multiple="props.multiple"
    collapse-tags
    :max-collapse-tags="2"
  />
</template>
